import { CircleDashedIcon, CircleMinusIcon, CropIcon, EllipsisVerticalIcon, PencilLine, PlusIcon, RefreshCcwIcon, RepeatIcon, UserIcon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import FilePreviewer from './FilePreviewer';
import { Menu } from '@headlessui/react';
import { humanize, KitDocumentType, SubmissionKitTagsModel } from '../../submission_boards/component/SbModel';
import { KitDocumentModel, SubmissionKitDocumentModel } from './KitModels';
import api from '../../../lib/api';
import ViewMediaModel from './ViewMediaModel';
import Button from '../../submission_boards/component/Button';
import media_bg_sm_image from '../../../assets/images/media_bg_sm.png';

interface SkItemProp {
  allowEdit: boolean;
  allowFileNameEdit: boolean;
  hideEmptyTags: boolean;
  submissionKitTag: SubmissionKitTagsModel;
  dragOverId: string;
  submissionKitDocuments: SubmissionKitDocumentModel[];
  upgradeUser?: boolean;
  defaultImageUrl?: string;
  isDefaultImageLocked?: boolean; // If true, prevents editing when using default image
  handleDragOver: (event: React.DragEvent<HTMLDivElement>, targetId: string) => Promise<void>;
  handleDrop: (event: any, tag: string) => Promise<void>;
  removeFromSubmissionKitClickHandler: (tag: string) => Promise<void>;
  cropImageClickHandler?: (submissionKitDocument: SubmissionKitDocumentModel) => Promise<void>;
  removeCropImageClickHandler?: (submissionKitDocument: SubmissionKitDocumentModel) => Promise<void>;
  showMediaKitForSk: (isReplace: boolean) => Promise<void>;
  upgradeClickedHandler: () => Promise<void>;
}

const SkTagItem: React.FC<SkItemProp> = ({
  submissionKitTag,
  dragOverId,
  submissionKitDocuments,
  allowEdit,
  allowFileNameEdit,
  hideEmptyTags,
  upgradeUser,
  defaultImageUrl,
  isDefaultImageLocked,
  handleDragOver,
  handleDrop,
  removeFromSubmissionKitClickHandler,
  cropImageClickHandler,
  removeCropImageClickHandler,
  showMediaKitForSk,
  upgradeClickedHandler,
}) => {
  const [submissionKitTagSelected, setSubmissionKitTagSelected] =
    useState<SubmissionKitTagsModel | null>(null);
  const [dragOverIdSelected, setDragOverIdSelected] = useState<string | null>(null);
  const [submissionKitDocumentsSelected, setSubmissionKitDocumentsSelected] = useState<
    SubmissionKitDocumentModel[]
  >([]);
  const [submissionKitDocumentsFiltered, setSubmissionKitDocumentsFiltered] = useState<
    SubmissionKitDocumentModel | null
  >(null);
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);
  const [editedFileName, setEditedFileName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [showMediaBig, setshowMediaBig] = useState<KitDocumentModel | null>(null);

  useEffect(() => {
    setDragOverIdSelected(dragOverId);
  }, [dragOverId]);

  useEffect(() => {
    console.log('upgradeUser', upgradeUser);
  }, [upgradeUser]);

  useEffect(() => {
    if (submissionKitTag != null) {
      console.log('SkTagItem:setSubmissionKitTagSelected', submissionKitTag);
      setSubmissionKitTagSelected(submissionKitTag);
    }
    if (submissionKitDocuments != null) {
      console.log('SkTagItem:setSubmissionKitDocumentsSelected', submissionKitDocuments);
      setSubmissionKitDocumentsSelected(submissionKitDocuments);
    }
    handleItemSetting(submissionKitTag, submissionKitDocuments);
  }, [submissionKitTag, submissionKitDocuments]);

  useEffect(() => {
    if (editingDocumentId !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingDocumentId]);

  const handleItemSetting = (_submissionKitTag, _submissionKitDocuments) => {
    if (
      _submissionKitTag != null &&
      _submissionKitDocuments != null &&
      _submissionKitDocuments.length > 0
    ) {
      let skMedias = _submissionKitDocuments.filter(
        (submissionKitDocument) =>
          submissionKitDocument.tag.toLowerCase() === _submissionKitTag.name.toLowerCase() &&
          !(submissionKitDocument.not_show ?? false)
      );
      // add only one element
      skMedias = skMedias.slice(0, 1);
      console.log('SkTagItem:skMedias', skMedias);
      setSubmissionKitDocumentsFiltered(skMedias.length >0 ? skMedias[0]: null);
    } else {
      console.log('SkTagItem:skMedias2 null');
      setSubmissionKitDocumentsFiltered(null);
    }
  };

  const updateFileName = async (documentId: number) => {
    if (!editedFileName.trim()) return setEditingDocumentId(null);

    try {
      const doc = submissionKitDocumentsFiltered;//.find((d) => d.kit_document.id === documentId);
      if (!doc) throw new Error('Document not found');

      await api.patch(`/api/v1/kit_documents/${doc.kit_document.uuid}/update_filename`, {
        filename: editedFileName,
      });
      setSubmissionKitDocumentsFiltered({ ...submissionKitDocumentsFiltered, kit_document: { ...submissionKitDocumentsFiltered.kit_document, file_name: editedFileName }});
      // setSubmissionKitDocumentsFiltered((prev) =>
      //   prev.map((d) =>
      //     d.kit_document.id === documentId
      //       ? { ...d, kit_document: { ...d.kit_document, file_name: editedFileName } }
      //       : d
      //   )
      // );
      setEditingDocumentId(null);
    } catch (error: any) {
      alert(error.response?.data?.error || error.message);
    }
  };
  if (hideEmptyTags && submissionKitDocumentsFiltered == null && !defaultImageUrl) {
    return <></>;
  }

  const isDefaultDocument = submissionKitDocumentsFiltered != null && submissionKitDocumentsFiltered.id === -1;
  const shouldShowMenu = allowEdit && 
                       submissionKitDocumentsFiltered != null && 
                       submissionKitDocumentsFiltered.id > 0 && 
                       !isDefaultImageLocked && 
                       !isDefaultDocument;

  const getHeightWidth = () => {
    let heightWidth = '';
    if (upgradeUser && !allowFileNameEdit) {
      if (submissionKitTagSelected.filetype == KitDocumentType.Image) {
        heightWidth += ' h-44-2 w-36-1 ';
      } else if (submissionKitTagSelected.filetype == KitDocumentType.Video) {
        heightWidth += ' h-28-1 w-48-1 ';
      }
    } else {
      if (submissionKitTagSelected.filetype == KitDocumentType.Image) {
        heightWidth += ' h-56-2 w-36-1 ';
      } else if (submissionKitTagSelected.filetype == KitDocumentType.Video) {
        heightWidth += ' h-36-2 w-48-1 ';
      }
    }

    // Always use consistent heights for all cards
    // if (submissionKitTagSelected.filetype == KitDocumentType.Image) {
    //   heightWidth += ' h-56-2 w-36-1 ';
    // } else if (submissionKitTagSelected.filetype == KitDocumentType.Video) {
    //   heightWidth += ' h-36-2 w-48-1 ';
    // }
    return heightWidth;
  }

  const getCardDimensions = () => {
    if (submissionKitTagSelected.filetype == KitDocumentType.Image) {
      if(allowEdit )
       return ' h-56-2 w-36-1 ';
      return " h-44-2 w-36-1 ";
    } else if (submissionKitTagSelected.filetype == KitDocumentType.Video) {
      if(allowEdit && allowFileNameEdit)
        return ' h-36-2 w-48-1 ';
      return " h-28-1 w-48-1 ";
    }
    return '';
  }
  return (
    <>
      {submissionKitTagSelected != null  && (
        <div
          className={`flex flex-col bg-blue-201 p-3 gap-2 p-3 rounded-2xl ring-0-0-0-2-4 hover:shadow-ring-0-0-0-2-4 ${
            dragOverIdSelected && dragOverIdSelected === submissionKitTagSelected.name
              ? ' bg-blue-200 border-blue-500 '
              : ''
          }`}
          onDragOver={async (event: any) => {
            console.log('onDragOver', submissionKitTagSelected.name);
            if (allowEdit && !isDefaultImageLocked) {
              await handleDragOver(event, submissionKitTagSelected.name);
            }
          }}
          onDrop={async (event: any) => {
            console.log('onDrop', submissionKitTagSelected.name);
            if (allowEdit && !isDefaultImageLocked) {
              await handleDrop(event, submissionKitTagSelected.name);
            }
          }}
        >
          <div className="w-full flex flex-row justify-between items-center gap-2-1">
            <div className="text-xs font-semibold flex justify-center items-center truncate">
              {humanize(submissionKitTagSelected.name)}
            </div>
            {/* menu start */}
            {true && (
              <Menu
                as="div"
                className={
                  'relative inline-block text-left w-8 h-8 ' +
                  (allowEdit &&
                  submissionKitDocumentsFiltered != null &&
                  submissionKitDocumentsFiltered.id > 0 &&
                  !isDefaultImageLocked
                    ? ' visible '
                    : ' invisible ')
                }
              >
                <Menu.Button
                  className={`flex  w-8 h-8 items-center justify-center transition hover:bg-gray-100 text-gray-500 rounded-full`}
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                    <EllipsisVerticalIcon className="w-4 h-4 text-grey" />
                  </div>
                </Menu.Button>

                <Menu.Items
                  className="absolute right-0 mt-2 w-48 origin-top-right 
                                                               rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 
                                                               focus:outline-none z-10"
                >
                  <div className="p-3 gap-1 flex flex-col">
                    {cropImageClickHandler && submissionKitDocumentsFiltered && 
                     submissionKitDocumentsFiltered.kit_document.file_content_type.startsWith('image') && (
                      <Menu.Item>
                        {({ active }) => (
                          <div
                            className="cursor-pointer px-2 py-1 gap-2-1 hover:bg-gray-100"
                            onClick={async (event: React.MouseEvent) => {
                              event.preventDefault();
                              if (cropImageClickHandler) {
                                await cropImageClickHandler(submissionKitDocumentsFiltered);
                              }
                            }}
                          >
                            <div className="flex flex-row items-center font-medium text-md p-0 gap-2">
                              <CropIcon className="w-4 h-4" />
                              Crop
                            </div>
                          </div>
                        )}
                      </Menu.Item>
                    )}
                    {removeCropImageClickHandler && submissionKitDocumentsFiltered && 
                     submissionKitDocumentsFiltered.kit_document.cropped_file_url && (
                      <Menu.Item>
                        {({ active }) => (
                          <div
                            className="cursor-pointer px-2 py-1 gap-2-1 hover:bg-gray-100"
                            onClick={async (event: React.MouseEvent) => {
                              event.preventDefault();
                              if (removeCropImageClickHandler) {
                                await removeCropImageClickHandler(submissionKitDocumentsFiltered);
                              }
                            }}
                          >
                            <div className="flex flex-row items-center font-medium text-md p-0 gap-2">
                              <RefreshCcwIcon className="w-4 h-4" />
                              Original Image
                            </div>
                          </div>
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <div
                          className="cursor-pointer px-2 py-1 gap-2-1 hover:bg-gray-100"
                          onClick={async (event: React.MouseEvent) => {
                            event.preventDefault();
                            if (showMediaKitForSk) {
                              showMediaKitForSk(true);
                            }
                            // await removeFromSubmissionKitClickHandler(
                            //   submissionKitTagSelected.name
                            // );
                            // setSubmissionKitDocumentsFiltered([]);
                          }}
                        >
                          <div className="flex flex-row items-center font-medium text-md p-0 gap-2">
                            <RepeatIcon className="w-4 h-4" />
                            Replace
                          </div>
                        </div>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <div
                          className="cursor-pointer px-2 py-1 gap-2-1 hover:bg-gray-100"
                          onClick={async (event: React.MouseEvent) => {
                            event.preventDefault();
                            await removeFromSubmissionKitClickHandler(
                              submissionKitTagSelected.name
                            );
                            setSubmissionKitDocumentsFiltered(null);
                          }}
                        >
                          <div className="flex flex-row items-center font-medium text-md p-0 gap-2">
                            <CircleMinusIcon className="w-4 h-4" />
                            Remove
                          </div>
                        </div>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Menu>
            )}
            {/* menu end */}
          </div>
          {/* empty no media tag start */}
          {allowEdit &&
            submissionKitDocumentsFiltered == null && ( //.length <= 0
              <>
                {/* Locked default image - non-pro users cannot edit */}
                {isDefaultImageLocked && defaultImageUrl ? (
                  <div className={`flex flex-col rounded-lg bg-white box-shadow-102 overflow-hidden ${getCardDimensions()}`}>
                    <div className="flex flex-1 flex-col overflow-hidden">
                      <FilePreviewer
                        dynamicHeight={false}
                        showControl={false}
                        file_url={defaultImageUrl}
                        file_name={'Default ' + humanize(submissionKitTagSelected.name)}
                        file_content_type={'image/jpeg'}
                      />

                      {/* <img 
                        src={defaultImageUrl} 
                        alt="Default" 
                        className="w-full h-full object-cover"
                      /> */}
                    </div>
                  </div>
                ) : (
                  /* Editable empty slot */
                  <div className={`flex flex-col rounded-lg border-2 border-dashed border-gray-400 ${getCardDimensions()}`}>
                    <div
                      className="flex flex-col cursor-pointer justify-center items-center rounded-lg h-full w-full overflow-hidden"
                      onClick={() => {
                        if (showMediaKitForSk) {
                          showMediaKitForSk(false);
                        }
                      }}
                    >
                      <PlusIcon className="w-6 h-6" />
                    </div>
                  </div>
                )}
              </>
            )}
          {/* empty no media tag end */}
          {/* show default image in view mode start */}
          {!allowEdit &&
            submissionKitDocumentsFiltered == null && 
            defaultImageUrl && (
              <>
                <div className={`flex flex-col rounded-lg bg-white box-shadow-102 overflow-hidden ${getCardDimensions()}`}>
                  <div
                    className="flex flex-1 flex-col cursor-pointer overflow-hidden"
                    onClick={() => {
                      const defaultDoc: KitDocumentModel = {
                        id: -1,
                        featured: 0,
                        created_at: new Date().toISOString(),
                        uuid: '',
                        record_id: -1,
                        file_url: defaultImageUrl,
                        file_name: 'Default ' + humanize(submissionKitTagSelected.name),
                        file_content_type: 'image/jpeg',
                        cropped_file_url: null,
                        cropped_file_name: null,
                        cropped_file_content_type: null,
                        work_kit_id: null,
                        wardrobe_kit_id: null,
                        hair_make_up_kit_id: null,
                      };
                      setshowMediaBig(defaultDoc);
                    }}
                  >
                    <FilePreviewer
                      dynamicHeight={false}
                      showControl={false}
                      file_url={defaultImageUrl}
                      file_name={'Default ' + humanize(submissionKitTagSelected.name)}
                      file_content_type={'image/jpeg'}
                    />
                  </div>
                </div>
              </>
            )}
          {/* show default image in view mode end */}
          {upgradeUser !== undefined && upgradeUser && (
            <>
              <div className="flex flex-col rounded-lg">
                <div
                  className={
                    'flex flex-col gap-4  justify-center items-center rounded-lg ' +
                    getHeightWidth()
                  }
                  style={{
                    backgroundImage: `url(${media_bg_sm_image})`, //`url(/assets/media_bg_sm.png)`,
                    backgroundSize: 'cover',
                  }}
                >
                  {submissionKitTagSelected &&
                    submissionKitTagSelected.filetype == KitDocumentType.Image && (
                      <div className="text-base font-medium text-white text-center">
                        Upgrade to Upload More!
                      </div>
                    )}
                  <Button
                    id="sk_upgrade_btn_id"
                    variant="primary"
                    btn_size="small"
                    icon_position="hor"
                    type="button"
                    onClick={() => {
                      if (upgradeClickedHandler) {
                        upgradeClickedHandler();
                      }
                    }}
                  >
                    <span className="truncate" style={{ maxWidth: '10rem' }}>
                      {'Upgrade'}
                    </span>
                  </Button>
                </div>
              </div>
            </>
          )}
          {/* show image start */}
          {
            (upgradeUser == undefined || upgradeUser == false) &&
              submissionKitDocumentsFiltered != null && (
                // submissionKitDocumentsFiltered.map((submissionKitDocument, index) => {
                //   return (
                <div
                  // key={index}
                  className={`flex flex-col rounded-lg bg-white box-shadow-102 overflow-hidden ${getCardDimensions()}`}
                >
                  <div
                    className="flex flex-1 flex-col cursor-pointer overflow-hidden"
                    onClick={() => {
                      setshowMediaBig(submissionKitDocumentsFiltered.kit_document);
                    }}
                  >
                    <FilePreviewer
                      dynamicHeight={false}
                      showControl={false}
                      file_url={
                        submissionKitDocumentsFiltered.kit_document.file_content_type.startsWith('image') ? 
                        (submissionKitDocumentsFiltered.kit_document.cropped_file_url ??
                        submissionKitDocumentsFiltered.kit_document.file_url) : 
                        submissionKitDocumentsFiltered.kit_document.file_url
                        
                      }
                      poster_url={
                        submissionKitDocumentsFiltered.kit_document.file_content_type.startsWith('image') ? '' : 
                        submissionKitDocumentsFiltered.kit_document.cropped_file_url
                      }
                      file_name={submissionKitDocumentsFiltered.kit_document.file_name}
                      file_content_type={
                        submissionKitDocumentsFiltered.kit_document.file_content_type
                      }
                    />
                  </div>
                  {allowEdit && allowFileNameEdit && submissionKitDocumentsFiltered.id > 0 && !isDefaultImageLocked && !isDefaultDocument && (
                    <div className="text-gray-500 px-4 py-2-1 w-full flex flex-row justify-between items-center gap-2-1">
                      {editingDocumentId === submissionKitDocumentsFiltered.kit_document.id ? (
                        <input
                          ref={inputRef}
                          type="text"
                          value={editedFileName}
                          onChange={(e) => setEditedFileName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              updateFileName(submissionKitDocumentsFiltered.kit_document.id);
                            if (e.key === 'Escape') setEditingDocumentId(null);
                          }}
                          onBlur={() =>
                            updateFileName(submissionKitDocumentsFiltered.kit_document.id)
                          }
                          className="text-sm font-medium w-full border border-blue-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <>
                          <div 
                            className="text-sm font-medium flex justify-center items-center cursor-pointer hover:bg-gray-50 rounded p-1 -m-1 transition-colors select-none overflow-hidden"
                            onDoubleClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingDocumentId(submissionKitDocumentsFiltered.kit_document.id);
                              setEditedFileName(
                                submissionKitDocumentsFiltered.kit_document.file_name
                              );
                            }}
                            onClick={(e) => {
                              // Prevent single click from interfering with double click
                              e.preventDefault();
                            }}
                            title="Double-click to edit file name"
                          >
                            <span 
                              className="truncate block" 
                              style={{ direction: 'rtl', textAlign: 'left', unicodeBidi: 'plaintext' }}
                            >
                              {submissionKitDocumentsFiltered.kit_document.file_name}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingDocumentId(submissionKitDocumentsFiltered.kit_document.id);
                              setEditedFileName(
                                submissionKitDocumentsFiltered.kit_document.file_name
                              );
                            }}
                            onDoubleClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingDocumentId(submissionKitDocumentsFiltered.kit_document.id);
                              setEditedFileName(
                                submissionKitDocumentsFiltered.kit_document.file_name
                              );
                            }}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Click or double-click to edit file name"
                          >
                            <PencilLine className="h-4 w-4 text-gray-500 underline" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )
            //    );
            // })
          }
          {/* show image end */}
          {showMediaBig != null && (
            <>
              <ViewMediaModel
                kit_document={showMediaBig}
                dialogCloseHandler={function (): void {
                  setshowMediaBig(null);
                }}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default SkTagItem;